import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addCredential, editCredential, getCredentialById } from '../../../api/endpoints/system';
import type { Currency, ProviderType } from '../../../api/types/common';
import type { Credential, CredentialProvider, CredentialSalesType } from '../../../api/types/system';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { useFreshEditRow } from '../../../components/useFreshEditRow';

const PROVIDER_TYPES = ['Air', 'Hotel', 'Transfer', 'Coper', 'Bus', 'RentCar', 'Tour', 'Insurance'] as const;
const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP', 'CHF', 'KZT'] as const;
const SALES_TYPES = ['B2E', 'B2C'] as const;
/** Confirmed via swagger's `Credential.Provider` enum (api/types/system.ts). */
const PROVIDERS = [
  'Amadeus', 'Galileo', 'Sabre', 'TurkishAirlines', 'OnurAir', 'AtlasGlobal', 'Pegasus',
  'SunExpress', 'BoraJet', 'Arkman', 'Travelrobot', 'BiletBank', 'Iati', 'Paxdrive',
  'HotelRunner', 'AerTicket', 'Paximum', 'Coper', 'Gulf', 'MetroBus', 'Corendon', 'Ajet',
  'RateHawk', 'Turna', 'Yolcu360', 'Freebird', 'THYNDC', 'ArkmanTour', 'Tamamliyo',
  'Travelport', 'Namila', 'None',
] as const;

const schema = z.object({
  provider: z.string().min(1, 'Sağlayıcı gerekli'),
  providerType: z.string().optional(),
  salesType: z.string().optional(),
  currency: z.string().optional(),
  providerName: z.string().optional(),
  providerAssemblyName: z.string().optional(),
  endPointName: z.string().optional(),
  address: z.string().optional(),
  serviceUsername: z.string().optional(),
  servicePassword: z.string().optional(),
  serviceSecurityKey: z.string().optional(),
  creditCard: z.string().optional(),
  officeId: z.string().optional(),
  gsaId: z.coerce.number().optional(),
  isActive: z.boolean(),
  isTest: z.boolean(),
  isProviderServiceFee: z.boolean(),
  prebookingAllowed: z.boolean(),
  bookingAllowed: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  provider: '',
  providerType: '',
  salesType: '',
  currency: '',
  providerName: '',
  providerAssemblyName: '',
  endPointName: '',
  address: '',
  serviceUsername: '',
  servicePassword: '',
  serviceSecurityKey: '',
  creditCard: '',
  officeId: '',
  gsaId: undefined,
  isActive: true,
  isTest: false,
  isProviderServiceFee: false,
  prebookingAllowed: true,
  bookingAllowed: true,
};

/** Create/edit dialog for Provider Ayarları (Credential). Confirmed via local-backend
 *  swagger + live curl (see api/types/system.ts header).
 *
 *  `servicePassword`/`serviceSecurityKey` are treated as write-only per the task's security
 *  requirement: never pre-filled from the fetched row, and only sent in the request when
 *  the admin types a new value — blank means "leave unchanged". */
export function CredentialFormDialog({
  open,
  credential,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  credential: Credential | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!credential;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error
  // (Get*ById can 200 with a business-logic null-ref for some accounts' data).
  const seed = useFreshEditRow({
    open,
    editing: credential,
    queryKey: ['system', 'credential', credential?.id],
    fetch: async (row) => (await getCredentialById(row.id)).credentials?.[0],
  });

  useEffect(() => {
    if (!open) return;
    if (!seed) {
      reset(emptyValues);
      return;
    }
    reset({
      provider: seed.provider ?? '',
      providerType: seed.providerType ?? '',
      salesType: seed.salesType ?? '',
      currency: seed.currency ?? '',
      providerName: seed.providerName ?? '',
      providerAssemblyName: seed.providerAssemblyName ?? '',
      endPointName: seed.endPointName ?? '',
      address: seed.address ?? '',
      serviceUsername: seed.serviceUsername ?? '',
      servicePassword: '',
      serviceSecurityKey: '',
      creditCard: seed.creditCard ?? '',
      officeId: seed.officeId ?? '',
      gsaId: seed.gsaId,
      isActive: !!seed.isActive,
      isTest: !!seed.isTest,
      isProviderServiceFee: !!seed.isProviderServiceFee,
      prebookingAllowed: !!seed.prebookingAllowed,
      bookingAllowed: !!seed.bookingAllowed,
    });
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const { servicePassword, serviceSecurityKey, ...rest } = values;
      const typed = {
        ...rest,
        provider: rest.provider as CredentialProvider,
        providerType: rest.providerType as ProviderType,
        salesType: rest.salesType as CredentialSalesType,
        currency: rest.currency as Currency,
      };
      if (isEdit && credential) {
        const payload: Credential = { ...(seed ?? credential), ...typed };
        if (servicePassword) payload.servicePassword = servicePassword;
        if (serviceSecurityKey) payload.serviceSecurityKey = serviceSecurityKey;
        return editCredential(payload);
      }
      return addCredential({ ...typed, servicePassword, serviceSecurityKey });
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Provider ayarı güncellendi.' : 'Provider ayarı oluşturuldu.', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['system', 'credentials'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Provider Ayarını Güncelle' : 'Yeni Provider Ayarı'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="provider"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Sağlayıcı (Provider)"
                    error={!!errors.provider}
                    helperText={errors.provider?.message}
                  >
                    <MenuItem value="">Seçiniz</MenuItem>
                    {PROVIDERS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="providerType"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Provider Tipi">
                    <MenuItem value="">Seçiniz</MenuItem>
                    {PROVIDER_TYPES.map((pt) => (
                      <MenuItem key={pt} value={pt}>
                        {pt}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Provider Adı" {...register('providerName')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="salesType"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Satış Tipi">
                    <MenuItem value="">Seçiniz</MenuItem>
                    {SALES_TYPES.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="currency"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Para Birimi">
                    <MenuItem value="">Seçiniz</MenuItem>
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Firma ID (GsaId)"
                {...register('gsaId')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Assembly Adı" {...register('providerAssemblyName')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Endpoint Adı" {...register('endPointName')} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Adres" {...register('address')} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth size="small" label="Servis Kullanıcı Adı" {...register('serviceUsername')} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={isEdit ? 'Servis Şifresi (değiştirmek için doldurun)' : 'Servis Şifresi'}
                {...register('servicePassword')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={isEdit ? 'Güvenlik Anahtarı (değiştirmek için doldurun)' : 'Güvenlik Anahtarı'}
                {...register('serviceSecurityKey')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Kredi Kartı" {...register('creditCard')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Office ID" {...register('officeId')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Aktif"
                  />
                )}
              />
              <Controller
                control={control}
                name="isTest"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Test"
                  />
                )}
              />
              <Controller
                control={control}
                name="prebookingAllowed"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Prebooking İzni"
                  />
                )}
              />
              <Controller
                control={control}
                name="bookingAllowed"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Booking İzni"
                  />
                )}
              />
              <Controller
                control={control}
                name="isProviderServiceFee"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Provider Servis Ücreti"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
